// index.tsx – React entrypoint (FRONTEND ONLY)

// ✅ MUST be first: initializes Firebase app/auth/db/functions before any other imports run.
import './src/lib/firebase';
import './i18n';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import 'leaflet/dist/leaflet.css';

import App from './App';
import './src/index.css';
import LandingPage from './src/components/LandingPage';
import PublicTermsPage from './src/components/PublicTermsPage';
import PublicPrivacyPage from './src/components/PublicPrivacyPage';
import LearnApp from './src/learn/LearnApp';
import PublicPostPreviewPage from './src/components/PublicPostPreviewPage';

// ---------------------------------------------------------------------------
// BUILD FINGERPRINT (must be visible in Safari Web Inspector or we're stale)
// ---------------------------------------------------------------------------
declare const __BUILD_ID__: string;

try {
  (window as any).__RODDO_BUILD_ID__ = __BUILD_ID__;
  console.log('[Roddo] BUILD_ID', __BUILD_ID__);
} catch {}

// Native crash breadcrumbs: plain window fields only (no React state, no rerenders).
// App.tsx updates __RODDO_ACTIVE_ROUTE__; this entrypoint records the most recent
// user interaction so a device-only fatal screen has enough context to reproduce.
let roddoLastInteraction = 'boot';

function describeInteractionTarget(target: EventTarget | null): string {
  try {
    const el = target instanceof Element ? target : null;
    if (!el) return 'unknown';
    const tag = String(el.tagName || '').toLowerCase() || 'element';
    const id = el.id ? `#${el.id}` : '';
    const role = el.getAttribute('role');
    const aria = el.getAttribute('aria-label');
    const text = String((el as HTMLElement).innerText || '').trim().replace(/\s+/g, ' ').slice(0, 80);
    const hint = aria || role || text;
    return `${tag}${id}${hint ? ` (${hint})` : ''}`;
  } catch {
    return 'unknown';
  }
}

function recordInteraction(kind: string, target: EventTarget | null) {
  roddoLastInteraction = `${kind}: ${describeInteractionTarget(target)}`;
}

window.addEventListener('pointerdown', (ev) => recordInteraction('pointerdown', ev.target), true);
window.addEventListener('keydown', (ev) => {
  if (ev.key === 'Enter' || ev.key === ' ') recordInteraction(`key:${ev.key === ' ' ? 'space' : ev.key}`, ev.target);
}, true);

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('❌ Root element #root not found in index.html');

const isNative = (() => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
})();

const IS_DEV = !!import.meta.env.DEV;

const resolveInitialThemeMode = (): 'light' | 'dark' => {
  try {
    const stored = window.localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {}
  return 'light';
};

const applyInitialThemeMode = () => {
  if (typeof document === 'undefined') return;
  const mode = resolveInitialThemeMode();
  const root = document.documentElement;
  const body = document.body;

  root.classList.toggle('dark', mode === 'dark');
  root.dataset.theme = mode;
  root.style.colorScheme = mode;

  if (body) {
    body.dataset.theme = mode;
    body.style.colorScheme = mode;
  }
};

applyInitialThemeMode();

// ✅ Only show fatal overlay on:
// - Native (helpful for device debugging), OR
// - Dev web (helpful during development)
// NEVER on prod web.
const ALLOW_FATAL_OVERLAY = isNative;

function isProbablyNetworkError(e: any): boolean {
  const msg = String(e?.message || e || '');
  return (
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('Load failed') ||
    msg.includes('ERR_SSL_PROTOCOL_ERROR') ||
    msg.includes('The network connection was lost') ||
    msg.includes('AbortError')
  );
}

function isProbablyExtensionOrInjectedResource(url: string): boolean {
  const u = String(url || '');
  return (
    u.startsWith('chrome-extension://') ||
    u.startsWith('safari-extension://') ||
    u.startsWith('moz-extension://') ||
    u.includes('browser-extension') ||
    u.includes('extensions')
  );
}

function safeErrorText(err: any): string {
  try {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;

    const parts: string[] = [];
    if (err?.name) parts.push(`name: ${String(err.name)}`);
    if (err?.code) parts.push(`code: ${String(err.code)}`);
    if (err?.message) parts.push(`message: ${String(err.message)}`);
    if (err?.stack) parts.push(`stack:\n${String(err.stack)}`);

    if (parts.length > 0) return parts.join('\n\n');

    return JSON.stringify(
      err,
      (_key, value) => {
        if (typeof value === 'function') return `[function ${value.name || 'anonymous'}]`;
        if (value instanceof Error) {
          return {
            name: value.name,
            message: value.message,
            stack: value.stack,
          };
        }
        return value;
      },
      2,
    );
  } catch (serializeError) {
    try {
      return String(err);
    } catch {
      return `Unable to serialize thrown value: ${String(serializeError)}`;
    }
  }
}

function showFatalOverlay(err: any) {
  if (!ALLOW_FATAL_OVERLAY) return;

  // Never fatal-overlay network-ish failures — they are common on web/FB browsers.
  if (!isNative && isProbablyNetworkError(err)) return;

  try {
    const msg = safeErrorText(err);
    const activeRoute = (() => {
      try {
        return JSON.stringify((window as any).__RODDO_ACTIVE_ROUTE__ ?? null);
      } catch {
        return 'unavailable';
      }
    })();
    const buildId = (() => {
      try {
        return String((window as any).__RODDO_BUILD_ID__ || 'unknown');
      } catch {
        return 'unknown';
      }
    })();
    const fatalContext = [
      `build: ${buildId}`,
      `platform: ${Capacitor.getPlatform?.() || 'unknown'}`,
      `route: ${activeRoute}`,
      `last interaction: ${roddoLastInteraction}`,
      `url: ${window.location.pathname}${window.location.search}${window.location.hash}`,
      `time: ${new Date().toISOString()}`,
    ].join('\n');

    let overlay = document.getElementById('roddo-fatal-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'roddo-fatal-overlay';
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.zIndex = '999999';
      overlay.style.background = 'white';
      overlay.style.color = 'black';
      overlay.style.padding = 'calc(env(safe-area-inset-top, 0px) + 16px) 16px 16px';
      overlay.style.fontFamily = 'ui-monospace, Menlo, monospace';
      overlay.style.fontSize = '12px';
      overlay.style.lineHeight = '1.45';
      overlay.style.whiteSpace = 'pre-wrap';
      overlay.style.overflow = 'auto';
      document.body.appendChild(overlay);
    }

    overlay.textContent = `RODDO FATAL ERROR:\n\n${fatalContext}\n\n${msg}`;
  } catch {
    // ignore
  }
}

// ---- HARDENED ERROR LOGGING (Web-safe) ----

// Resource-load failures (script/link/img)
window.addEventListener(
  'error',
  (ev: any) => {
    const target = ev?.target;
    const isResource =
      target &&
      (target.tagName === 'SCRIPT' || target.tagName === 'LINK' || target.tagName === 'IMG');

    if (isResource) {
      const src = target.src || null;
      const href = target.href || null;
      const url = src || href || '';

      const details = {
        type: 'resource-error',
        tag: target.tagName,
        src,
        href,
        rel: target.rel || null,
        as: target.as || null,
      };

      // Image misses are non-fatal UI fallbacks, not application errors.
      // In particular, old Google-auth avatar URLs can expire or stop resolving.
      // Recover those centrally so every existing avatar surface (Admin, profile,
      // map marker, crew, etc.) gets a safe Roddo fallback without request loops.
      if (target.tagName === 'IMG') {
        try {
          const img = target as HTMLImageElement;
          const failedUrl = String(img.currentSrc || img.src || url || '');
          const isGoogleAvatar = /(^|\.)googleusercontent\.com(?:\/|$)/i.test(
            (() => {
              try {
                return new URL(failedUrl, window.location.href).hostname;
              } catch {
                return failedUrl;
              }
            })(),
          );

          if (isGoogleAvatar && img.dataset.roddoAvatarFallback !== '1') {
            img.dataset.roddoAvatarFallback = '1';
            img.src = '/avatar-default.png';
            return;
          }
        } catch {}

        // Do not flood DevTools with ordinary remote image failures. Browsers may
        // still expose the failed request in Network, while real JS/script errors
        // remain visible below.
        return;
      }

      // Script/link failures are actionable and should stay noisy.
      console.error('[GLOBAL resource.error]', details);

      // On web prod, do NOT fatal overlay for extension or injected resource failures.
      if (!ALLOW_FATAL_OVERLAY) return;
      if (!isNative && isProbablyExtensionOrInjectedResource(url)) return;

      // Only script load failures are truly startup-fatal.
      if (target.tagName === 'SCRIPT') showFatalOverlay(details);
      return;
    }

    // JS runtime errors.
    // Some WKWebView/Android WebView startup hiccups dispatch a bare Event through
    // window.onerror with no message, filename, line, column, or Error object. That
    // is not an app crash; it is usually a generic resource/media/WebView event that
    // reached this capture listener. Log it for diagnostics, but never block Roddo
    // behind the fatal screen for "[object Event]".
    const hasRealJsError = !!ev?.error || !!ev?.message || !!ev?.filename || !!ev?.lineno || !!ev?.colno;
    const isBareEvent =
      !hasRealJsError &&
      typeof Event !== 'undefined' &&
      ev instanceof Event &&
      (ev.currentTarget === window || ev.target === window || ev.target === document);

    const errObj = ev?.error || ev?.message || (hasRealJsError ? ev : null);
    const details = {
      type: 'js-error',
      message: ev?.message || null,
      filename: ev?.filename || null,
      lineno: ev?.lineno || null,
      colno: ev?.colno || null,
      eventType: ev?.type || null,
      targetTag: ev?.target?.tagName || null,
      targetId: ev?.target?.id || null,
      targetClass: ev?.target?.className || null,
      errorString: errObj ? String(errObj) : null,
      errorStack: ev?.error?.stack ? String(ev.error.stack) : null,
      ignoredBareEvent: isBareEvent,
    };

    console.error('[GLOBAL window.error]', details);

    if (isBareEvent || !hasRealJsError) return;

    // On web prod: never fatal overlay.
    // On dev/native: show overlay unless it’s network-ish.
    showFatalOverlay(ev?.error || ev?.message || details);
  },
  true // capture phase required for resource errors
);

window.addEventListener('unhandledrejection', (ev: any) => {
  const reason = ev?.reason;
  const details = {
    type: 'unhandledrejection',
    reasonString: reason ? String(reason) : null,
    reasonStack: reason?.stack ? String(reason.stack) : null,
  };

  console.error('[GLOBAL unhandledrejection]', details);

  // Always suppress network-ish rejections on web so they never escalate.
  if (!isNative && isProbablyNetworkError(reason)) {
    try {
      ev.preventDefault();
    } catch {}
    return;
  }

  showFatalOverlay(reason || details);
});

// After first tick, dump loaded resources (Safari debugging)
setTimeout(() => {
  try {
    const perf = (performance.getEntriesByType?.('resource') || []) as any[];
    const scripts = perf.filter((r) => r.initiatorType === 'script').slice(-12);
    const links = perf.filter((r) => r.initiatorType === 'link').slice(-12);
    console.debug('[BOOT] recent script resources', scripts.map((r) => r.name));
    console.debug('[BOOT] recent link resources', links.map((r) => r.name));
  } catch (e) {
    console.debug('[BOOT] perf dump failed', e);
  }
}, 250);

// Normalize trailing slashes
const rawPath = window.location.pathname.replace(/\/+$/, '') || '/';

// --- NATIVE SAFETY GUARD: block gapi on iOS WKWebView (but DO NOT crash the app) ---
function stubGoogleGlobalsForNative() {
  const w = window as any;

  if (!w.gapi) {
    w.gapi = {
      load: (...args: any[]) => console.warn('[native] gapi.load blocked/stubbed', args),
      client: {},
      auth2: {},
    };
  }

  if (!w.google) w.google = {};
  if (!w.google.accounts) w.google.accounts = {};
  if (!w.google.accounts.id) {
    w.google.accounts.id = {
      initialize: (...args: any[]) =>
        console.warn('[native] google.accounts.id.initialize blocked/stubbed', args),
      prompt: (...args: any[]) =>
        console.warn('[native] google.accounts.id.prompt blocked/stubbed', args),
      renderButton: (...args: any[]) =>
        console.warn('[native] google.accounts.id.renderButton blocked/stubbed', args),
      disableAutoSelect: (...args: any[]) =>
        console.warn('[native] google.accounts.id.disableAutoSelect blocked/stubbed', args),
      revoke: (...args: any[]) => console.warn('[native] google.accounts.id.revoke blocked/stubbed', args),
    };
  }
}

if (isNative && typeof document !== 'undefined') {
  stubGoogleGlobalsForNative();

  const blockHosts = ['apis.google.com'];

  const wrapAppend = (parent: Node) => {
    const orig = parent.appendChild.bind(parent);
    parent.appendChild = ((node: any) => {
      try {
        const tag = node?.tagName;
        const src = node?.src || node?.getAttribute?.('src') || '';
        if (tag === 'SCRIPT' && typeof src === 'string' && blockHosts.some((h) => src.includes(h))) {
          console.warn(`[native] blocked script injection: ${src}`);
          return node;
        }
      } catch {}
      return orig(node);
    }) as any;
  };

  wrapAppend(document.head);
  wrapAppend(document.body);
}

// ✅ /learn should work in dev without forcing /app
const isLearnPath = rawPath === '/learn' || rawPath.startsWith('/learn/');
const isPublicPostPath = rawPath.startsWith('/app/p/');

// In native: force /app
// In dev web: force /app unless explicitly on /terms, /privacy, /learn, /app/p/:id
const path = isNative
  ? '/app'
  : IS_DEV && rawPath !== '/terms' && rawPath !== '/privacy' && !isLearnPath && !isPublicPostPath
    ? '/app'
    : rawPath;

const AppTree = (
  <Suspense
    fallback={
      <div
        className="fixed inset-0 overflow-hidden bg-[#dfeff6]"
        style={{ colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light' }}
      >
        <img
          src="/assets/RoddoPrivacyMistScene.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          draggable={false}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,.06)_0%,rgba(255,255,255,.01)_60%,rgba(20,48,71,.22)_100%)]" />
        <div className="absolute inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+34px)] flex justify-center px-6">
          <div className="h-1.5 w-full max-w-[280px] overflow-hidden rounded-full bg-white/50 ring-1 ring-[#173A57]/10">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-[#2F80ED]/80" />
          </div>
        </div>
      </div>
    }
  >
    {path === '/terms' ? (
      <PublicTermsPage />
    ) : path === '/privacy' ? (
      <PublicPrivacyPage />
    ) : isLearnPath ? (
      <LearnApp />
    ) : isPublicPostPath ? (
      <PublicPostPreviewPage />
    ) : path === '/app' ? (
      <App />
    ) : (
      <LandingPage
        onOpenApp={() => (window.location.href = '/app')}
        onSignIn={() => (window.location.href = '/app')}
        onOpenTerms={() => (window.location.href = '/terms')}
        onOpenPrivacy={() => (window.location.href = '/privacy')}
      />
    )}
  </Suspense>
);

const root = createRoot(rootElement);

try {
  root.render(!isNative && IS_DEV ? <React.StrictMode>{AppTree}</React.StrictMode> : AppTree);
} catch (e) {
  console.error('[FATAL root.render]', e);
  showFatalOverlay(e);
}